import React, { useState, useMemo } from 'react';
import { FiChevronUp, FiChevronDown, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import StatusBadge from './StatusBadge';

const DataTable = ({
  columns,
  data,
  loading,
  onRowClick,
  renderActions,
  pageSize = 10,
  striped = true,
}) => {
  const [sortConfig, setSortConfig] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  // Reset to first page when data changes (e.g., filter applied)
  React.useEffect(() => {
    setCurrentPage(0);
  }, [data]);

  // Sort data
  const sortedData = useMemo(() => {
    let sorted = [...data];
    if (sortConfig) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal == null) return 1;
        if (bVal == null) return -1;

        if (typeof aVal === 'string') {
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      });
    }
    return sorted;
  }, [data, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (key) => {
    setSortConfig(prevConfig => {
      if (prevConfig?.key === key) {
        return {
          key,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
    setCurrentPage(0);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">📋</div>
        <div className="empty-state-title">No data</div>
        <div className="empty-state-text">No records found matching your criteria</div>
      </div>
    );
  }

  const renderCellValue = (value, column, row) => {
    if (column.type === 'status') {
      return <StatusBadge status={value} />;
    }
    if (column.render) {
      return column.render(value, row);
    }
    return value;
  };

  return (
    <div>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map(column => (
                <th
                  key={column.key}
                  className={column.sortable ? 'sortable' : ''}
                  onClick={() => column.sortable && handleSort(column.key)}
                  style={{ width: column.width }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {column.headerRender ? column.headerRender() : column.label}
                    {column.sortable && (
                      <span style={{ opacity: 0.5 }}>
                        {sortConfig?.key === column.key ? (
                          sortConfig.direction === 'asc' ? <FiArrowUp /> : <FiArrowDown />
                        ) : (
                          <FiChevronDown style={{ opacity: 0.2 }} />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              {renderActions && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, index) => (
              <tr
                key={row._key || row.id || index}
                onClick={(e) => { if (e.target.type === 'checkbox' || e.target.type === 'number') return; onRowClick && onRowClick(row); }}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {columns.map(column => (
                  <td key={column.key}>
                    {renderCellValue(row[column.key], column, row)}
                  </td>
                ))}
                {renderActions && (
                  <td>
                    <div className="table-actions">
                      {renderActions(row)}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            disabled={currentPage === 0}
            onClick={() => setCurrentPage(0)}
            className={currentPage === 0 ? 'disabled' : ''}
          >
            First
          </button>
          <button
            disabled={currentPage === 0}
            onClick={() => setCurrentPage(currentPage - 1)}
            className={currentPage === 0 ? 'disabled' : ''}
          >
            Prev
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pageNum = i;
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={currentPage === pageNum ? 'active' : ''}
              >
                {pageNum + 1}
              </button>
            );
          })}
          {totalPages > 5 && <span>...</span>}
          <button
            disabled={currentPage === totalPages - 1}
            onClick={() => setCurrentPage(currentPage + 1)}
            className={currentPage === totalPages - 1 ? 'disabled' : ''}
          >
            Next
          </button>
          <button
            disabled={currentPage === totalPages - 1}
            onClick={() => setCurrentPage(totalPages - 1)}
            className={currentPage === totalPages - 1 ? 'disabled' : ''}
          >
            Last
          </button>
          <span style={{ marginLeft: '10px', fontSize: '12px', color: '#7f8c8d' }}>
            Page {currentPage + 1} of {totalPages}
          </span>
        </div>
      )}
    </div>
  );
};

export default DataTable;
